// src/components/OCRUpload.js
// Reusable OCR upload component - Phase-3A + Phase-3B
// Uploads file to /api/ocr/extract, fetches confidence from /api/ocr/prefill
// Shows preview + raw text + confidence badge, returns results to parent

import React, { useState } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { 
  FileText, ChevronDown, ChevronUp, Loader2, ExternalLink, 
  AlertTriangle, CheckCircle2, AlertCircle, HelpCircle, Brain
} from 'lucide-react';

// Confidence badge component - extracted to avoid render-time definition
const ConfidenceBadge = ({ confidence, loadingConfidence }) => {
  if (loadingConfidence) {
    return (
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking history...
      </div>
    );
  }

  if (!confidence) return null;

  const level = confidence.confidence;
  const reason = confidence.confidence_reason;

  const configs = {
    HIGH: {
      color: 'bg-green-100 text-green-800 border-green-200',
      icon: CheckCircle2,
      iconColor: 'text-green-600',
    },
    MEDIUM: {
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      icon: HelpCircle,
      iconColor: 'text-yellow-600',
    },
    LOW: {
      color: 'bg-red-100 text-red-800 border-red-200',
      icon: AlertCircle,
      iconColor: 'text-red-600',
    },
  };

  const config = configs[level] || configs.LOW;
  const Icon = config.icon;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Brain className="h-4 w-4 text-muted-foreground" />
        <Badge variant="outline" className={`${config.color} text-xs`}>
          <Icon className={`h-3 w-3 mr-1 ${config.iconColor}`} />
          {level} Confidence
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground pl-6">
        {reason}
      </p>
      {level === 'LOW' && (
        <Alert variant="warning" className="py-1.5 mt-2">
          <AlertTriangle className="h-3 w-3" />
          <AlertDescription className="text-xs">
            Please verify all fields carefully
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

/**
 * OCRUpload Component - Phase-3B Enhanced
 * 
 * Props:
 * - contextType: string - "COIL_IN" | "RECOVERY_LOG" | "DISPATCH" | "FINALIZE"
 * - partyId?: string - vendor/party ID for confidence learning
 * - onResult: (result: { rawText: string, fileUrl: string, confidence?: object }) => void
 * - label?: string - custom label text
 * - accept?: string - file accept pattern (default: image/*,application/pdf)
 */
const OCRUpload = ({ 
  contextType, 
  partyId = null, 
  onResult, 
  label = "Upload Slip (OCR)",
  accept = "image/*,application/pdf"
}) => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [showOCRText, setShowOCRText] = useState(false);
  const [error, setError] = useState(null);
  
  // Phase-3B: Confidence state
  const [confidence, setConfidence] = useState(null);
  const [loadingConfidence, setLoadingConfidence] = useState(false);

  // Fetch confidence after OCR extraction
  const fetchConfidence = async (rawText) => {
    if (!rawText) return;
    
    setLoadingConfidence(true);
    try {
      const prefillRes = await api.ocr.prefill({
        context_type: contextType,
        vendor_or_party_id: partyId || null,
        raw_text: rawText,
      });
      setConfidence(prefillRes.data);
    } catch (err) {
      console.warn('Confidence fetch failed (non-critical):', err);
      setConfidence(null);
    } finally {
      setLoadingConfidence(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB allowed.');
      return;
    }

    setUploading(true);
    setError(null);
    setConfidence(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.ocr.extract(formData);
      const data = response.data;

      const newResult = {
        fileUrl: data.file_url,
        rawText: data.raw_text || '',
        provider: data.provider,
        success: data.success,
      };
      setResult(newResult);

      // Fetch confidence scoring (Phase-3B)
      if (data.raw_text) {
        await fetchConfidence(data.raw_text);
      }

      // Notify parent component
      if (onResult) {
        onResult({
          rawText: data.raw_text || '',
          fileUrl: data.file_url,
        });
      }

      if (data.success && data.raw_text) {
        toast.success('OCR extraction complete');
        setShowOCRText(true);
      } else if (!data.success) {
        toast.warning('File uploaded, but OCR extraction failed. You can still proceed.');
      } else {
        toast.info('File uploaded. No text detected by OCR.');
      }

    } catch (err) {
      console.error('OCR upload failed:', err);
      const errorMsg = err.response?.data?.detail || 'Upload failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setShowOCRText(false);
    setConfidence(null);
    if (onResult) {
      onResult({ rawText: '', fileUrl: '' });
    }
  };

  return (
    <div className="space-y-3" data-testid={`ocr-upload-${contextType.toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <Label className="text-sm font-medium">{label}</Label>
      </div>

      <Alert className="py-2 border-amber-300 bg-amber-50 dark:bg-amber-950/20">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-xs text-amber-800 dark:text-amber-300">
          OCR feature is in testing phase. Please verify extracted values before saving.
        </AlertDescription>
      </Alert>

      {!result ? (
        <div className="flex items-center gap-2">
          <Input
            type="file"
            accept={accept}
            onChange={handleFileChange}
            disabled={uploading}
            className="flex-1"
            data-testid="ocr-file-input"
          />
          {uploading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing...
            </div>
          )}
        </div>
      ) : (
        <div className="border rounded-lg p-3 bg-slate-50 dark:bg-slate-900 space-y-3">
          {/* File preview link */}
          <div className="flex items-center justify-between">
            <a 
              href={result.fileUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" />
              View uploaded file
            </a>
            <Button 
              type="button" 
              size="sm" 
              variant="outline" 
              onClick={handleReset}
            >
              Change file
            </Button>
          </div>

          {/* Phase-3B: Confidence Badge */}
          <ConfidenceBadge confidence={confidence} loadingConfidence={loadingConfidence} />

          {/* OCR status */}
          {!result.success && (
            <Alert variant="warning" className="py-2">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                OCR extraction unavailable. File saved but no text extracted.
              </AlertDescription>
            </Alert>
          )}

          {/* Collapsible OCR text panel */}
          {result.rawText && (
            <Collapsible open={showOCRText} onOpenChange={setShowOCRText}>
              <CollapsibleTrigger asChild>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  className="w-full justify-between"
                >
                  <span className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    OCR Text ({result.rawText.length} chars)
                  </span>
                  {showOCRText ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 p-3 bg-white dark:bg-slate-800 border rounded text-xs font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {result.rawText}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Provider: {result.provider}
                </p>
              </CollapsibleContent>
            </Collapsible>
          )}

          {!result.rawText && result.success && (
            <p className="text-xs text-muted-foreground">
              No text detected in the uploaded file.
            </p>
          )}
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="py-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-xs">{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default OCRUpload;
